import { format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

import {
  hasRefreshTokenPointer,
  hasVaultPointer,
  isReconnectError,
} from '@/components/settings/integration/utils';
import type { ConnectedAccount } from '@/hooks/usePlatformIntegrations';
import { friendlyConnectionErrorMessage } from '@/lib/errors/friendly-errors';
import { scopeGapReason } from '@/lib/platforms/scope-gap';

export type StatusKind = 'connected' | 'needs-reconnect' | 'expiring' | 'error';

export interface DerivedStatus {
  kind: StatusKind;
  label: string;
  dotClass: string;
  textClass: string;
  tooltip: string;
}

/**
 * Single source of truth for "is this connected account healthy" — reused by
 * ConnectedIntegrationRow (Settings) and the sidebar AccountsGroup so both
 * surfaces agree on health semantics byte-for-byte (CRITIQUE.md W8).
 *
 * Extracted verbatim (same 4 branches, same 7-day expiry threshold, same
 * shopify/google-drive vault-pointer exemption) from the formerly-private
 * `deriveStatus` in ConnectedIntegrationRow.tsx. Zero behavior change there.
 */
export function deriveConnectionStatus(account: ConnectedAccount): DerivedStatus {
  if (account.error) {
    // A bad/lost token is a reconnect, not a fault — amber, one-click re-auth.
    if (isReconnectError(account.error)) {
      return {
        kind: 'needs-reconnect',
        label: 'Reconnect',
        dotClass: 'bg-warning',
        textClass: 'text-warning',
        tooltip: 'Lost connection to this account — reconnect to resume syncing.',
      };
    }
    return {
      kind: 'error',
      label: 'Error',
      dotClass: 'bg-destructive',
      textClass: 'text-destructive',
      tooltip: friendlyConnectionErrorMessage(account.error),
    };
  }
  const scopeGap = scopeGapReason(account);
  if (scopeGap) {
    return {
      kind: 'needs-reconnect',
      label: 'Reconnect',
      dotClass: 'bg-warning',
      textClass: 'text-warning',
      tooltip: scopeGap,
    };
  }
  if (
    !hasVaultPointer(account.rawData) &&
    account.platform !== 'shopify' &&
    account.platform !== 'google-drive'
  ) {
    return {
      kind: 'needs-reconnect',
      label: 'Reconnect',
      dotClass: 'bg-warning',
      textClass: 'text-warning',
      tooltip: 'Connection is missing credentials — reconnect to fix.',
    };
  }
  // Access-token expiry is only the USER's problem when we cannot refresh it
  // ourselves. Google issues 1-hour access tokens, so keying "Expires soon" on
  // token_expires_at branded every fresh GA/YouTube/Business connection as
  // near-broken seconds after connecting (founder report 2026-07-20). With a
  // refresh token in the vault, real auth failure surfaces through last_error
  // (the branches above) — never through the raw TTL.
  if (account.tokenExpiresAt && !hasRefreshTokenPointer(account.rawData)) {
    const expiresAt = new Date(account.tokenExpiresAt);
    const now = new Date();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const formatted = format(expiresAt, 'MMM d, yyyy h:mm a');
    if (expiresAt.getTime() < now.getTime()) {
      return {
        kind: 'needs-reconnect',
        label: 'Reconnect',
        dotClass: 'bg-warning',
        textClass: 'text-warning',
        tooltip: `Token expired ${formatted} — reconnect to resume syncing.`,
      };
    }
    if (expiresAt.getTime() < now.getTime() + sevenDays) {
      return {
        kind: 'expiring',
        label: 'Expires soon',
        dotClass: 'bg-warning',
        textClass: 'text-warning',
        tooltip: `Token expires ${formatted}`,
      };
    }
  }
  return {
    kind: 'connected',
    // Row shows pure state; the exact "last synced" time lives on hover (tooltip)
    // and in the "Sync now" menu item — no need to duplicate it in the row label.
    label: 'Connected',
    dotClass: 'bg-success',
    textClass: 'text-muted-foreground',
    tooltip: account.lastSync
      ? `Last synced ${formatDistanceToNow(new Date(account.lastSync), { addSuffix: true })}`
      : 'Connected',
  };
}
